import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { initialValues, validationSchema } from './formData';
import { AlertProps } from './props';
import { cleanObj } from '../../../../../../shared/utils/objects';
import { ISelect, ParamEnum } from '../../../../../../shared/types/shared';
import { AlertSection } from '../../../../../../shared/types/legalProceedings/alerts';
import {
    asyncGetParamById,
    asyncGetParams,
} from '../../../../../../services/param.service';
import { asyncGetTemplateByCode } from '../../../../../../services/template.service';
import {
    Template,
    TemplateEnum,
} from '../../../../../../shared/types/template';

export const useAlert = ({
    data,
    onChangeData,
    onChangeFormik,
    canSendEmail,
    receivedThrough,
}: AlertProps) => {
    const [typesStatus, setTypesStatus] = useState<ISelect[]>([]);
    const [typesAlertDate, setTypesAlertDate] = useState<ISelect[]>([]);
    const [typesClassification, setTypesClassification] = useState<ISelect[]>(
        []
    );
    const [templates, setTemplates] = useState<Template[]>();
    const [areas, setAreas] = useState<ISelect[]>([]);

    const onSubmit = (data: AlertSection) => {
        if (onChangeData) onChangeData(cleanObj(data) || {});
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit,
    });

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        formik?.setFieldValue(
            'alertDate',
            (data?.alertDate as ISelect)?.id || ''
        );
        formik?.setFieldValue(
            'classification',
            (data?.classification as ISelect)?.id || ''
        );
        formik?.setFieldValue('numberDays', data?.numberDays || '');
        formik?.setFieldValue('alertByMail', data?.alertByMail || false);
        formik?.setFieldValue('emailOfficials', data?.emailOfficials || '');
        formik?.setFieldValue('areaException', data?.areaException || []);
        formik?.setFieldValue('tags', (data?.tags as ISelect)?.id || '');
        formik?.setFieldValue('subject', data?.subject || '');
        formik?.setFieldValue(
            'template',
            (data?.template as Template)?.id || ''
        );
        formik?.setFieldValue('body', (data?.template as Template)?.body || '');
    }, [data]);

    useEffect(() => {
        asyncGetParams(ParamEnum.ALERTS_STATUS).then((params) =>
            setTypesStatus(params)
        );
        asyncGetParams(ParamEnum.ALERTS_ALERT_DATE).then((params) => {
            const sacDate = params.find(
                (p) => p.code === `${ParamEnum.ALERTS_ALERT_DATE}_SAC_DATE`
            );
            formik?.setFieldValue('alertDate', sacDate ? sacDate?.id : '');
            setTypesAlertDate(params);
        });
        asyncGetParams(ParamEnum.ALERTS_CLASSIFICATION).then((params) => {
            setTypesClassification(params);
            const typeDate = params.find(
                (p) =>
                    p.code ===
                    `${ParamEnum.ALERTS_CLASSIFICATION}_CALENDAR_DAYS`
            );
            formik?.setFieldValue(
                'classification',
                typeDate ? typeDate?.id : ''
            );
        });
    }, []);

    useEffect(() => {
        if (receivedThrough && typeof receivedThrough === 'string') {
            asyncGetParamById(receivedThrough).then((item) => {
                if (item) {
                    let template_type = '';
                    switch (item.name) {
                        case 'Directa':
                            template_type =
                                TemplateEnum.PQR_TRACKING_DIRECT_PROCESS;
                            break;
                        case 'Derecho de Petición':
                            template_type =
                                TemplateEnum.PQR_TRACKING_PETITION_PROCESS;
                            break;
                        case 'SFC':
                            template_type =
                                TemplateEnum.PQR_TRACKING_REQUIREMENT_SFC;
                            break;
                        case 'DCF':
                            template_type =
                                TemplateEnum.PQR_TRACKING_ADVOCATE_DCF;
                            break;
                        default:
                            break;
                    }

                    asyncGetTemplateByCode(template_type).then((res) => {
                        formik.setFieldValue('body', res?.body || '');
                        formik.setFieldValue('template', res?.id || '');
                        formik.setFieldValue('subject', res?.subject || '');

                        if (res) setTemplates([res]);
                    });
                }
            });
        }
    }, [receivedThrough]);

    useEffect(() => {
        if (!canSendEmail || !formik.values.alertByMail) {
            formik?.setFieldValue('alertByMail', false);
        }
    }, [canSendEmail, formik.values.alertByMail]);

    useEffect(() => {
        asyncGetParams(ParamEnum.PQR_REDIRECTION_AREA).then((items) =>
            setAreas(items)
        );
    }, []);

    return {
        formik,
        typesStatus,
        typesAlertDate,
        typesClassification,
        templates,
        areas,
    };
};
